import React from 'react'
import Image from 'next/legacy/image'
import { BaseText, MediumText } from 'components/common/text'
import dayjs from 'dayjs'
import styled from 'styled-components'
import { Column, Row } from 'components/common/layout'
import { EarthIcon, SproutIcon } from 'components/common/icon'
import { useBaseInfo } from 'apis/hooks'
import { getLocationName } from 'utils/displays'
import { Location } from 'types/common'
import { HiOutlineLocationMarker } from 'react-icons/hi'

const ICON_SIZE = 18

// 스타트업 투자정보 테이블 컨테이너
export const StartupTableInfo: React.FC<{
  data: {
    location?: Location
    funding?: {
      round: string // id 투자 단계
      amount: number | null // 투자 유치액
      attractedAt: string // 투자 유치 날짜
    }
    memberCount: number
    devCount: number
    hasCto: boolean
    serviceAreas: string[]
  }
}> = ({ data }) => {
  const { data: baseInfo } = useBaseInfo()
  const locationName = data?.location
    ? getLocationName(data.location, baseInfo?.locations || [])
    : ''

  return (
    <Table>
      {locationName && (
        <TableRow>
          <RowName>
            <HiOutlineLocationMarker size={ICON_SIZE} />
            <TitleText>위치</TitleText>
          </RowName>
          <DataValue>{locationName}</DataValue>
        </TableRow>
      )}
      <TableRow>
        <RowName>
          <EarthIcon size={ICON_SIZE} />
          <TitleText>서비스 분야</TitleText>
        </RowName>
        <DataValue>
          {data.serviceAreas && data.serviceAreas.length > 0
            ? data.serviceAreas.join(' ‧ ')
            : '-'}
        </DataValue>
      </TableRow>
      <TableRow>
        <RowName>
          <SproutIcon size={ICON_SIZE} />
          <TitleText>투자 단계</TitleText>
        </RowName>
        <DataValue>
          {data.funding?.round ? data.funding.round : '-'}
          {data.funding?.attractedAt &&
            ` (${dayjs(data.funding.attractedAt).format('YYYY.MM.')})`}
        </DataValue>
      </TableRow>
      <TableRow>
        <RowName>
          <Image
            src='/finance.svg'
            alt='finance'
            width={ICON_SIZE}
            height={ICON_SIZE}
          />
          <TitleText>누적 투자 유치액</TitleText>
        </RowName>
        <DataValue>
          {data.funding?.amount ? `${data.funding.amount}억` : '비공개'}
        </DataValue>
      </TableRow>
      <TableRow>
        <RowName>
          <Image
            src='/account-group-outline.svg'
            alt='account-group-outline'
            width={ICON_SIZE}
            height={ICON_SIZE}
          />
          <TitleText>전체 / 개발자</TitleText>
        </RowName>
        <DataValue>
          {data.memberCount ? `${data.memberCount}명` : '-'}
          {' / '}
          {data.devCount ? `${data.devCount}명` : '-'}
          {data.hasCto ? '(CTO 있음)' : ''}
        </DataValue>
      </TableRow>
    </Table>
  )
}

const Table = styled(Column)`
  width: 100%;
  border: 1px solid ${(p) => p.theme.color.line};
  padding: 0 20px;
  border-radius: 8px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 0 16px;
  }
`

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 16px;
  padding: 15px 0;
  :not(:last-child) {
    border-bottom: 1px solid ${(p) => p.theme.color.gray3};
  }
  @media ${(p) => p.theme.deviceSize.mobile} {
    grid-template-columns: 130px 1fr;
    padding: 12px 0;
  }
`

const RowName = styled(Row)`
  gap: 6px;
`

const DataValue = styled(BaseText)`
  color: ${(p) => p.theme.color.black2};
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.small};
  }
`

const TitleText = styled(MediumText)`
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.small};
  }
`
